/**
 * Equipos (rc104) — hooks del inventario de comodatos.
 * Lectura: /equipos/resumen + /equipos?vista=. La acción "marcar recuperado"
 * reusa el endpoint F92 existente (auditado).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiCall } from "@/lib/api";

export type EquiposResumen = {
  empresa_id: number | null;
  pendientes_recoleccion: number;
  comodato_en_campo: number;
  recuperados: number;
  propiedad_cliente: number;
};

export type EquipoItem = {
  contrato_id: string;
  codigo: string;
  estatus: string;
  cliente_id: string;
  cliente_nombre: string;
  empresa_id: number | null;
  equipo_descripcion: string | null;
  equipo_costo: string;
  mac: string | null;
  fecha_cancelacion: string | null;
  equipo_recuperado_at: string | null;
  recuperado_por_nombre: string | null;
  lat: number | null;
  lng: number | null;
  ubicacion_referencia: string | null;
};

export type EquiposVista = "pendientes" | "campo" | "recuperados";

export type EquiposList = {
  vista: EquiposVista;
  empresa_id: number | null;
  total: number;
  items: EquipoItem[];
};

const KEY = "equipos";

function empresaParam(empresaId: number | null): Record<string, number> {
  return empresaId !== null ? { empresa_id: empresaId } : {};
}

export function useEquiposResumen(empresaId: number | null) {
  return useQuery({
    queryKey: [KEY, "resumen", empresaId],
    queryFn: () =>
      apiCall<EquiposResumen>("get", "/api/v1/equipos/resumen", {
        searchParams: empresaParam(empresaId),
      }),
    staleTime: 30_000,
  });
}

export function useEquiposList(vista: EquiposVista, empresaId: number | null) {
  return useQuery({
    queryKey: [KEY, "list", vista, empresaId],
    queryFn: () =>
      apiCall<EquiposList>("get", "/api/v1/equipos", {
        searchParams: { vista, ...empresaParam(empresaId) },
      }),
    staleTime: 30_000,
  });
}

/** Marca el equipo de UN contrato como recuperado (endpoint F92, auditado). */
export function useMarcarRecuperado() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ contratoId, notas }: { contratoId: string; notas?: string }) =>
      apiCall("post", `/api/v1/contratos/${contratoId}/equipo-recuperado`, {
        json: { notas: notas || null },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY] });
      qc.invalidateQueries({ queryKey: ["contratos"] });
    },
  });
}
